import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { LayoutDashboard, Inbox, FileText, Edit3, LogOut, Users, ShieldCheck, KeyRound } from "lucide-react";

export default function AdminLayout() {
    const { user, logout, checking } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!checking && (!user || user.role !== "admin")) {
            navigate("/admin/login", {
                replace: true,
                state: { from: `${location.pathname}${location.search}` },
            });
        }
    }, [checking, user, navigate, location.pathname, location.search]);

    if (checking) {
        return <div className="min-h-screen flex items-center justify-center text-brand-muted">Checking session…</div>;
    }
    if (!user || user.role !== "admin") {
        return null;
    }

    const linkClass = ({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
            isActive ? "bg-brand-ink text-brand-paper" : "text-brand-muted hover:bg-brand-sand hover:text-brand-ink"
        }`;

    return (
        <div className="min-h-screen flex bg-brand-paper" data-testid="admin-layout">
            <aside className="w-64 border-r border-brand-line bg-brand-paper flex flex-col">
                <div className="p-6 border-b border-brand-line">
                    <div className="font-serif text-lg text-brand-ink">
                        Realtor<span className="italic text-brand-gold">Referral</span>
                    </div>
                    <div className="text-xs text-brand-muted mt-1">Admin · {user.email}</div>
                </div>
                <nav className="p-3 flex-1 space-y-1">
                    <NavLink to="/admin" end className={linkClass} data-testid="admin-nav-dashboard">
                        <LayoutDashboard size={16} /> Dashboard
                    </NavLink>
                    <NavLink to="/admin/leads" className={linkClass} data-testid="admin-nav-leads">
                        <Inbox size={16} /> Leads
                    </NavLink>
                    <NavLink to="/admin/agents" className={linkClass} data-testid="admin-nav-agents">
                        <Users size={16} /> Agents
                    </NavLink>
                    <NavLink to="/admin/blog" className={linkClass} data-testid="admin-nav-blog">
                        <FileText size={16} /> Journal
                    </NavLink>
                    <NavLink to="/admin/content" className={linkClass} data-testid="admin-nav-content">
                        <Edit3 size={16} /> Site Text
                    </NavLink>
                    <NavLink to="/admin/privacy" className={linkClass} data-testid="admin-nav-privacy">
                        <ShieldCheck size={16} /> Data Requests
                    </NavLink>
                    <NavLink to="/admin/account" className={linkClass} data-testid="admin-nav-account">
                        <KeyRound size={16} /> Account
                    </NavLink>
                </nav>
                <div className="p-3 border-t border-brand-line">
                    <button
                        onClick={() => { logout(); navigate("/admin/login"); }}
                        data-testid="admin-logout-button"
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-brand-muted hover:bg-brand-sand hover:text-brand-ink transition-colors"
                    >
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </aside>
            <main className="flex-1 overflow-x-auto">
                <Outlet />
            </main>
        </div>
    );
}
